import { loadBootstrapRecord, type DeviceBootstrapRecord } from "./bootstrap";
import { fetchThroughOHTTP } from "./ohttp";

type AnalyticsChannel = "site" | "client";
type AnalyticsValue = string | number | boolean | null | undefined;
type AnalyticsProperties = Record<string, AnalyticsValue>;

type AnalyticsInitOptions = {
  channel: AnalyticsChannel;
  surface: string;
  version?: string;
};

type AnalyticsSession = {
  userId?: string | null;
  signedInAt?: string | null;
};

const DEFAULT_API_BASE_URL = "https://api.key.store";
const DEFAULT_CAPTURE_PATH = "/v1/analytics/posthog/capture";
const DEFAULT_SITE_SURFACE = "marketing_site";
const DEFAULT_CLIENT_SURFACE = "web_client";
const ANALYTICS_ID_STORAGE_KEY = "key.store.analytics.distinct_id";
const FORBIDDEN_PROPERTY_NAMES = new Set(["email", "$email", "username", "$username", "name", "$name"]);

let initialized = false;
let lifecycleEventsInstalled = false;
let lastContext: AnalyticsProperties = {};
let activeAccountDistinctID: string | null = null;
let lastBootstrapDeviceID: string | null = null;

function readStringEnv(name: string, fallback = "") {
  return (import.meta.env[name] as string | undefined)?.trim() || fallback;
}

function readBooleanEnv(name: string, fallback = false) {
  const raw = (import.meta.env[name] as string | undefined)?.trim().toLowerCase();
  if (!raw) {
    return fallback;
  }
  return raw === "1" || raw === "true" || raw === "yes" || raw === "on";
}

function configuredApiBaseURL() {
  return readStringEnv(
    "VITE_KEY_STORE_API_BASE_URL",
    readStringEnv("VITE_API_BASE_URL", DEFAULT_API_BASE_URL),
  ).replace(/\/+$/, "");
}

function configuredCapturePath() {
  return readStringEnv("VITE_POSTHOG_CAPTURE_PATH", DEFAULT_CAPTURE_PATH);
}

function analyticsDisabled() {
  return readBooleanEnv("VITE_POSTHOG_DISABLED", false);
}

function analyticsReady() {
  return typeof window !== "undefined" && !analyticsDisabled();
}

function deviceProps(record: DeviceBootstrapRecord): AnalyticsProperties {
  return {
    key_store_device_id: record.deviceId,
    key_store_device_platform: record.platform,
    key_store_device_channel: record.channel,
    key_store_device_version: record.version,
    key_store_device_attestation_status: record.attestationStatus,
    key_store_device_attestation_provider: record.attestationProvider,
    key_store_device_registered_at: record.registeredAt,
  };
}

function registerContext(context: AnalyticsProperties) {
  lastContext = sanitizedProperties({ ...lastContext, ...context });
}

function restoreBootstrapContext() {
  const bootstrap = loadBootstrapRecord();
  if (bootstrap) {
    registerContext(deviceProps(bootstrap));
  }
}

function anonymousDistinctID() {
  try {
    const existing = window.localStorage.getItem(ANALYTICS_ID_STORAGE_KEY);
    if (existing && validDistinctID(existing)) {
      return existing;
    }
    const generated = `anon_${window.crypto?.randomUUID?.() ?? randomFallbackID()}`;
    window.localStorage.setItem(ANALYTICS_ID_STORAGE_KEY, generated);
    return generated;
  } catch {
    return `anon_${randomFallbackID()}`;
  }
}

function activeDistinctID() {
  return activeAccountDistinctID ?? anonymousDistinctID();
}

function accountDistinctID(userId: string) {
  const normalized = userId.trim();
  if (!validDistinctID(normalized)) {
    return null;
  }
  return normalized.startsWith("account_") ? normalized : `account_${normalized}`;
}

function randomFallbackID() {
  const random =
    window.crypto?.getRandomValues ?
      window.crypto.getRandomValues(new Uint32Array(4))
    : Uint32Array.from({ length: 4 }, () => Math.floor(Math.random() * 0xffffffff));
  return Array.from(random, (value) => value.toString(16).padStart(8, "0")).join("");
}

function validDistinctID(value: string) {
  return value.length > 0 && value.length <= 160 && !value.includes("@") && !/[\s\p{C}]/u.test(value);
}

function sanitizedProperties(properties: AnalyticsProperties) {
  const sanitized: Record<string, string | number | boolean | null> = {};
  for (const [key, value] of Object.entries(properties)) {
    if (!key || FORBIDDEN_PROPERTY_NAMES.has(key.toLowerCase()) || value === undefined) {
      continue;
    }
    sanitized[key] = value;
  }
  return sanitized;
}

function pageProperties(): AnalyticsProperties {
  return {
    $current_url: window.location.href,
    pathname: window.location.pathname,
    referrer: document.referrer || undefined,
    title: document.title || undefined,
  };
}

async function capturePostHogEvent(event: string, properties: AnalyticsProperties = {}) {
  if (!initialized || !analyticsReady()) {
    return false;
  }

  const payload = {
    distinct_id: activeDistinctID(),
    event,
    properties: sanitizedProperties({
      ...lastContext,
      ...properties,
      key_store_analytics_transport: "ohttp",
    }),
    timestamp: new Date().toISOString(),
  };

  try {
    const response = await fetchThroughOHTTP(configuredApiBaseURL(), configuredCapturePath(), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });
    return response.ok;
  } catch (error) {
    if (import.meta.env.DEV) {
      console.debug("key.store analytics capture failed", error);
    }
    return false;
  }
}

function capturePageview() {
  void capturePostHogEvent("$pageview", pageProperties());
}

function installLifecycleEvents() {
  if (lifecycleEventsInstalled || !analyticsReady()) {
    return;
  }
  lifecycleEventsInstalled = true;

  const emitPageview = () => window.setTimeout(capturePageview, 0);
  const originalPushState = window.history.pushState.bind(window.history);
  const originalReplaceState = window.history.replaceState.bind(window.history);
  window.history.pushState = ((data: unknown, unused: string, url?: string | URL | null) => {
    originalPushState(data, unused, url);
    emitPageview();
  }) as typeof window.history.pushState;
  window.history.replaceState = ((data: unknown, unused: string, url?: string | URL | null) => {
    originalReplaceState(data, unused, url);
    emitPageview();
  }) as typeof window.history.replaceState;
  window.addEventListener("popstate", capturePageview);
  window.addEventListener("pagehide", () => {
    void capturePostHogEvent("$pageleave", pageProperties());
  });
}

export function surfaceForChannel(channel: AnalyticsChannel) {
  if (channel === "client") {
    return readStringEnv("VITE_POSTHOG_CLIENT_SURFACE", DEFAULT_CLIENT_SURFACE);
  }
  return readStringEnv("VITE_POSTHOG_SITE_SURFACE", DEFAULT_SITE_SURFACE);
}

export function initWebAnalytics(options: AnalyticsInitOptions) {
  if (!analyticsReady()) {
    return false;
  }

  initialized = true;
  registerContext({
    key_store_product: "key.store",
    key_store_channel: options.channel,
    key_store_surface: options.surface,
    key_store_environment: import.meta.env.MODE,
    key_store_app_version: options.version ?? readStringEnv("VITE_APP_VERSION", "0.0.0"),
    key_store_origin: window.location.origin,
    key_store_host: window.location.host,
  });
  restoreBootstrapContext();
  installLifecycleEvents();
  capturePageview();
  return true;
}

export function syncAnalyticsBootstrapRecord(record: DeviceBootstrapRecord | null) {
  if (!record || !initialized || !analyticsReady()) {
    return;
  }
  registerContext(deviceProps(record));
  if (lastBootstrapDeviceID !== record.deviceId) {
    lastBootstrapDeviceID = record.deviceId;
    void capturePostHogEvent("key_store_device_bootstrapped");
  }
}

export function syncAnalyticsSession(session: AnalyticsSession | null) {
  if (!initialized || !analyticsReady()) {
    return;
  }

  if (!session) {
    activeAccountDistinctID = null;
    restoreBootstrapContext();
    void capturePostHogEvent("key_store_session_signed_out");
    return;
  }

  const distinctID = session.userId ? accountDistinctID(session.userId) : null;
  if (!distinctID) {
    return;
  }

  activeAccountDistinctID = distinctID;
  restoreBootstrapContext();
  void capturePostHogEvent("key_store_session_identified", {
    key_store_signed_in_at: session.signedInAt ?? undefined,
  });
}
